import RestClientService from "@/core/services/restClient.service";
import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { CREDIT_LIMIT_MANAGEMENT_API_PATH } from "@/modules/CreditLimitManagement/constants";
import { CreditLimitRequestsResponse } from "@/modules/CreditLimitManagement/models/fetchCreditLimitRequests.response";
import { CreditLimitManagementMapper } from "@/modules/CreditLimitManagement/responseMappers/creditLimitManagement.mapper";
import { IPaginatedCreditLimitRequests } from "@/core/interfaces/state/creditLimitRequest.interface";
import { FetchCreditLimitRequestsParamsRequest } from "@/modules/CreditLimitManagement/models/fetchCreditLimitRequestsParams.request";
import { CreateCreditLimitRequestsBody } from "@/modules/CreditLimitManagement/models/createCreditLimitRequestsBody.request";
import { CreateCreditLimitRequestResponse } from "@/modules/CreditLimitManagement/models/createCreditLimitRequests.response";
import { formatString } from "@/core/utils/helpers";
import { CreditLimitRequestDetailResponse } from "@/modules/CreditLimitManagement/models/creditLimitRequestDetail.response";
import { ICreditLimitRequestDetail } from "@/core/interfaces/state/creditLimitRequestDetail.interface";
import { ProcessCreditLimitRequestBody } from "@/modules/CreditLimitManagement/models/processCreditLimitRequestBody.request";

class CreditLimitManagementService {
  private restClient: RestClientService;

  constructor() {
    this.restClient = new RestClientService();
  }

  public async fetchCreditLimitRequests(
    params: FetchCreditLimitRequestsParamsRequest,
  ): Promise<IPaginatedCreditLimitRequests> {
    const creditLimitManagementMapper: CreditLimitManagementMapper =
      new CreditLimitManagementMapper();
    try {
      const response: ICoreResponse<CreditLimitRequestsResponse> =
        await this.restClient.get(CREDIT_LIMIT_MANAGEMENT_API_PATH.GET, params);

      if (response.success && response.data) {
        creditLimitManagementMapper.mapCreditLimitRequestsResponse(
          response.data,
        );
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (err) {
      console.error("Error in fetchCreditLimitRequests:", err);
    }
    return creditLimitManagementMapper.getCreditLimitRequests();
  }

  public async fetchCreditLimitRequestDetail(
    id: string,
  ): Promise<ICreditLimitRequestDetail> {
    "CreditLimitManagementService.fetchCreditLimitRequestDetail";
    const creditLimitManagementMapper: CreditLimitManagementMapper =
      new CreditLimitManagementMapper();

    try {
      const response: ICoreResponse<CreditLimitRequestDetailResponse> =
        await this.restClient.get(
          formatString(CREDIT_LIMIT_MANAGEMENT_API_PATH.GET_BY_ID, id),
        );

      if (response.success && response.data) {
        creditLimitManagementMapper.mapCreditLimitRequestDetailResponse(
          response.data,
        );
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (err) {
      console.error("Error in fetchCreditLimitRequestDetail:", err);
    }
    return creditLimitManagementMapper.getCreditLimitRequestDetail();
  }

  public async createCreditLimitRequest(
    body: CreateCreditLimitRequestsBody,
    callback?: () => void,
  ): Promise<void> {
    try {
      const response: ICoreResponse<CreateCreditLimitRequestResponse> =
        await this.restClient.post(CREDIT_LIMIT_MANAGEMENT_API_PATH.POST, body);

      if (response.success && response.data) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.CREATE_CREDIT_LIMIT_REQUEST_SUCCESS;
        if (callback) {
          callback();
        }
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch (err) {
      console.error("Error in createCreditLimitRequest:", err);
    }
    notificationService.triggerNotification();
  }

  public async processCreditLimitRequest(
    body: ProcessCreditLimitRequestBody,
  ): Promise<void> {
    try {
      const response: ICoreResponse<CreateCreditLimitRequestResponse> =
        await this.restClient.post(
          CREDIT_LIMIT_MANAGEMENT_API_PATH.POST_PROCESS,
          body,
        );

      if (response.success && response.data) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        if (body.status) {
          notificationService.description =
            NOTIFICATION_MESSAGES.APPROVE_CREDIT_LIMIT_REQUEST_SUCCESS;
        } else {
          notificationService.description =
            NOTIFICATION_MESSAGES.REJECT_CREDIT_LIMIT_REQUEST_SUCCESS;
        }
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch (err) {
      console.error("Error in processCreditLimitRequest:", err);
    }
    notificationService.triggerNotification();
  }

  public async fetchCreditLimitSlabs(): Promise<any> {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      response = await this.restClient.get(
        CREDIT_LIMIT_MANAGEMENT_API_PATH.GET_CREDIT_LIMIT_SLABS,
      );
      if (response.success && response.data) {
        return response.data?.data?.slabs || [];
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (err) {
      console.error("Error in fetchCreditLimitSlabs:", err);
    }
  }
}

export default CreditLimitManagementService;
